// shared/components/ui/form-field/FormFieldInput.tsx

import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { Input } from '@/shared/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/shared/components/ui/input-group';
import { FloatingLabelInput } from './FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';
import type { floatingInputVariants } from './FloatingLabelInput';

// Re-exporta el tipo de variante para uso externo
export type FloatingInputVariant = VariantProps<typeof floatingInputVariants>['variant'];

interface FormFieldInputProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  placeholder?: string;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endElement?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  // Props para label flotante
  floatingLabel?: boolean;
  floatingVariant?: FloatingInputVariant;
  /**
   * Hide the visible label. For a field whose row already renders its own label —
   * the convert-to-task dialog does — this avoids showing two.
   *
   * The control keeps its accessible name: `label` is emitted as `aria-label`, so
   * the field is named even when nothing outside supplies one. A wrapper's own
   * `<label htmlFor>` stays valid alongside it; both carry the same text.
   */
  hideLabel?: boolean;
}

export function FormFieldInput<T extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  autoComplete,
  placeholder,
  disabled,
  startIcon,
  endElement,
  className,
  'data-testid': testId,
  floatingLabel = false,
  floatingVariant = 'default',
  hideLabel = false,
}: FormFieldInputProps<T>) {
  const hasWrapper = !!startIcon || !!endElement;

  // Only when the visible label is actually suppressed. The floating variant renders
  // its own label, so it needs no substitute.
  const ariaLabel = hideLabel && !floatingLabel ? label : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {/* FieldLabel solo se muestra si NO es floating y no se pidió ocultar */}
          {!floatingLabel && !hideLabel && <FieldLabel htmlFor={name}>{label}</FieldLabel>}

          {floatingLabel ? (
            // Caso floating label — standalone, sin InputGroup por ahora
            <FloatingLabelInput
              {...field}
              id={name}
              type={type}
              autoComplete={autoComplete}
              disabled={disabled}
              label={label}
              variant={floatingVariant}
              // Fix: string vacío evalúa como sin valor
              hasValue={field.value !== '' && field.value !== undefined && field.value !== null}
              invalid={fieldState.invalid}
              endElement={endElement} // ← ahora se pasa al FloatingLabelInput
              className={className}
              data-testid={testId}
            />
          ) : hasWrapper ? (
            // Caso con iconos — usa InputGroup
            <InputGroup data-disabled={disabled}>
              {startIcon && <InputGroupAddon align="inline-start">{startIcon}</InputGroupAddon>}
              <InputGroupInput
                {...field}
                id={name}
                type={type}
                autoComplete={autoComplete}
                placeholder={placeholder}
                disabled={disabled}
                aria-invalid={fieldState.invalid}
                aria-label={ariaLabel}
                value={(field.value as string) ?? ''}
                className={className}
                data-testid={testId}
              />
              {endElement && <InputGroupAddon align="inline-end">{endElement}</InputGroupAddon>}
            </InputGroup>
          ) : (
            // Caso base — Input simple
            <Input
              {...field}
              id={name}
              type={type}
              autoComplete={autoComplete}
              placeholder={placeholder}
              disabled={disabled}
              aria-invalid={fieldState.invalid}
              aria-label={ariaLabel}
              value={(field.value as string) ?? ''}
              className={className}
              data-testid={testId}
            />
          )}

          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
